import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import { Text } from '@antimatterhq/ui/components/typography';
import { Button } from '@antimatterhq/ui/components/button';
import { BaseHomepageSummaryCard } from '@/components/homepage/summaryCard/BaseHomepageSummaryCard';

type HomepageSummaryCardSkeletonProps = {
  title: string;
  to: string;
  showActionButton?: boolean;
  actionButtonText?: string;
  showChart?: boolean;
  showStatus?: boolean;
  showUnassigned?: boolean;
  totalsMetric?: string;
  subtitle?: string;
};

export const HomepageSummaryCardSkeleton = ({
  title,
  subtitle,
  to,
  showActionButton = false,
  actionButtonText = 'View All',
  showChart = false,
  showStatus = false,
  showUnassigned = false,
  totalsMetric = 'Items',
}: HomepageSummaryCardSkeletonProps) => {
  return (
    <BaseHomepageSummaryCard title={title} to={to}>
      <div className="grid grid-cols-3 gap-4">
        {!showChart ? (
          <>
            {/* Main Metric */}
            <div className="flex flex-col gap-2">
              <Text>{subtitle}</Text>
              <Skeleton className="h-8 w-24" />
            </div>

            {/* Last 7 Days Trend */}
            <div className="flex flex-col gap-2">
              <Text>Total {totalsMetric} last 7 days</Text>
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-16" />
              </div>
            </div>

            {/* Last 24 Hours Trend */}
            <div className="flex flex-col gap-2">
              <Text>Total {totalsMetric} last 24 hours</Text>
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-16" />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="col-span-2 grid grid-cols-2 gap-4">
              {/* Main Metric */}
              <div className="flex flex-col gap-2">
                <Text>Total {title}</Text>
                <Skeleton className="h-8 w-24" />
              </div>

              {/* Last 7 Days Trend */}
              <div className="flex flex-col gap-2">
                <Text>Total {totalsMetric} last 7 days</Text>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-8 w-16" />
                </div>
              </div>

              {/* Status Section */}
              {showStatus && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-6 w-14" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-6 w-14" />
                  </div>
                </div>
              )}

              {/* Last 24 Hours Trend */}
              <div className="flex flex-col gap-2">
                <Text>Total {totalsMetric} last 24 hours</Text>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-8 w-16" />
                </div>
              </div>
            </div>

            {/* Chart Area */}
            <div className="col-span-1 flex h-full flex-col">
              <Text className="mb-4">Data processed last 7 days</Text>
              <Skeleton className="mt-auto h-full max-h-[80%] w-full" />
            </div>
          </>
        )}
      </div>

      {/* Footer Section */}
      {(showActionButton || showUnassigned) && (
        <div className="mt-4 flex w-full justify-between">
          {showUnassigned && (
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-16" />
                <Text>Unassigned</Text>
              </div>
            </div>
          )}
          {showActionButton && (
            <div className={`${showUnassigned ? '' : 'ml-auto'}`}>
              <Button variant="default" leftIcon="lucide:folder-open" disabled>
                {actionButtonText}
              </Button>
            </div>
          )}
        </div>
      )}
    </BaseHomepageSummaryCard>
  );
};

export default HomepageSummaryCardSkeleton;
